import { devWarn } from "./log";

const MUTE_KEY = "duckify_muted"; // non-sensitive UI preference ("0"/"1"), fine to persist in localStorage
const QUACK_SRC = "/quack.mp3";
const DONE_PREFIX = "(Done) "; // background-tab completion signal in the page title
const VOLUME = 0.6; // moderate, single short quack

let audio = null;
const handled = new Set(); // in-memory dedup: `${duckId}:${reason}` events already signalled (resets on a full reload)
let pendingHiddenPlay = false; // a completion fired while hidden/blocked — play it when the tab becomes visible

const store = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* storage unavailable (private mode) */ } },
};

export const isMuted = () => store.get(MUTE_KEY) === "1"; // default: key absent ⇒ NOT muted ⇒ sound on
export const setMuted = (muted) => {
  store.set(MUTE_KEY, muted ? "1" : "0");
  try { window.dispatchEvent(new Event("duckify-mute-change")); } catch { /* no window */ }
};

const getAudio = () => {
  if (!audio) {
    audio = new Audio(QUACK_SRC);
    audio.preload = "auto";
    audio.volume = VOLUME;
  }
  return audio;
};

// Call from a user gesture (first click / key press) so later playback isn't blocked by autoplay rules.
export const primeQuack = () => {
  let a;
  try { a = getAudio(); } catch { return; }
  try {
    a.muted = true;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => { a.pause(); a.currentTime = 0; a.muted = false; }).catch(() => { a.muted = false; });
    } else {
      a.pause();
      a.muted = false;
    }
  } catch { try { a.muted = false; } catch { /* ignore */ } }
};

// Install ONCE: unlock audio on the very first user interaction anywhere (covers "Make your duck" or any button).
let unlockInstalled = false;
export const installQuackUnlock = () => {
  if (unlockInstalled || typeof window === "undefined") return;
  unlockInstalled = true;
  const onFirst = () => {
    primeQuack();
    window.removeEventListener("pointerdown", onFirst);
    window.removeEventListener("keydown", onFirst);
  };
  window.addEventListener("pointerdown", onFirst);
  window.addEventListener("keydown", onFirst);
};

const titleShow = () => {
  if (typeof document === "undefined" || !document.hidden) return;
  if (!document.title.startsWith(DONE_PREFIX)) document.title = DONE_PREFIX + document.title;
};
const titleClear = () => {
  if (typeof document === "undefined") return;
  if (document.title.startsWith(DONE_PREFIX)) document.title = document.title.slice(DONE_PREFIX.length);
};

const rawPlay = () => {
  let a;
  try { a = getAudio(); } catch { return false; }
  try {
    a.muted = false;
    a.currentTime = 0;
    const p = a.play();
    if (p && typeof p.catch === "function") {
      p.catch((e) => { pendingHiddenPlay = true; devWarn("Quack playback blocked", e?.message); });
    }
    return true;
  } catch (e) {
    pendingHiddenPlay = true;
    devWarn("Quack playback error", e?.message);
    return false;
  }
};

// Mark an event key as already signalled WITHOUT playing — used to avoid quacking a state the duck is ALREADY in
// when polling starts (page reload / re-open); only live transitions should quack.
export const markQuacked = (key) => { if (key) handled.add(key); };

// THE single reusable helper. `reason` is a label ("upload"|"render"|"views"|"ready"|"repaint"|...). Pass `once`
// (e.g. `${duckId}:${reason}`) so a repeated trigger (build polling, re-render, tab switch) quacks at most once.
export const playQuack = (reason = "generic", { once = null, force = false } = {}) => {
  if (once) {
    if (handled.has(once)) return false;
    handled.add(once); // mark handled even when muted, so a later unmute can't replay a past completion
  }
  if (typeof document !== "undefined" && document.hidden) titleShow(); // background tab: also signal in the title
  if (isMuted() && !force) return false;
  return rawPlay();
};

// When the tab becomes visible: clear the title badge and play any quack that was blocked while hidden.
if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) return;
    titleClear();
    if (pendingHiddenPlay && !isMuted()) rawPlay();
    pendingHiddenPlay = false;
  });
}

// Test-only: reset the in-memory dedup + pending state between cases (never called in production use).
export const _resetQuackState = () => { handled.clear(); pendingHiddenPlay = false; };
